import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TFunction } from 'i18next';
import { ReactNode, useContext, useMemo } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { sfmViewerColors } from '../../../../viewer/elements/design/colors';
import { Icon } from '../../icon/icon';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { List } from '../../list/list';
import { Select } from '../../select/select';
import { Stack } from '../../stack/stack';
import { AlignerContext, LegendsMode } from '../gcp-page/aligner-state';

const getLegendsModeOptions = (t: TFunction): { id: LegendsMode; name: string }[] => {
  return [
    {
      id: 'default',
      name: t('legendsList.options.default', { ns: 'cloudProcessing' }),
    },
    {
      id: 'accuracy',
      name: t('legendsList.options.rtkAccuracy', { ns: 'cloudProcessing' }),
    },
    {
      id: 'iso',
      name: 'ISO',
    },
    {
      id: 'shutterSpeed',
      name: t('legendsList.options.shutterSpeed', { ns: 'cloudProcessing' }),
    },
    {
      id: 'aperture',
      name: t('legendsList.options.aperture', { ns: 'cloudProcessing' }),
    },
  ];
};

export const GeodataLegendList = () => {
  const { legendsMode, setLegendsMode } = useContext(AlignerContext);
  const { t } = useTranslation();
  const legendsModeOptions = useMemo(() => getLegendsModeOptions(t), [t]);

  const imageItems = useMemo<NonNullable<ReactNode>[]>(() => {
    if (legendsMode === 'accuracy') {
      return [
        <LegendItem
          color={sfmViewerColors.legends.excellent}
          icon={['fas', 'circle-small']}
          key="excellent"
          label={`(<= 0.025) ${t('legendsList.values.excellent', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.ok}
          icon={['fas', 'circle-small']}
          key="caution"
          label={`(0.025-0.05) ${t('legendsList.values.good', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.critical}
          icon={['fas', 'circle-small']}
          key="critical"
          label={`(> 0.05) ${t('legendsList.values.poor', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.none}
          icon={['fas', 'circle-small']}
          key="none"
          label={`(> 10) ${t('legendsList.values.noRtk', { ns: 'cloudProcessing' })}`}
        />,
      ];
    }

    if (legendsMode === 'iso') {
      return [
        <LegendItem
          color={sfmViewerColors.legends.none}
          icon={['fas', 'circle-small']}
          key="none"
          label="None"
        />,
        <LegendItem
          color={sfmViewerColors.legends.excellent}
          icon={['fas', 'circle-small']}
          key="excellent"
          label={`(<= 200) ${t('legendsList.values.excellent', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.ok}
          icon={['fas', 'circle-small']}
          key="ok"
          label={`(<= 400) ${t('legendsList.values.ok', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.caution}
          icon={['fas', 'circle-small']}
          key="caution"
          label={`(<= 600) ${t('legendsList.values.caution', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.critical}
          icon={['fas', 'circle-small']}
          key="critical"
          label={`(> 600) ${t('legendsList.values.critical', { ns: 'cloudProcessing' })}`}
        />,
      ];
    }

    if (legendsMode === 'aperture') {
      return [
        <LegendItem
          color={sfmViewerColors.legends.none}
          icon={['fas', 'circle-small']}
          key="none"
          label={t('legendsList.values.none', { ns: 'cloudProcessing' })}
        />,
        <LegendItem
          color={sfmViewerColors.legends.caution}
          icon={['fas', 'circle-small']}
          key="caution-high"
          label={`(f/2) ${t('legendsList.values.caution', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.ok}
          icon={['fas', 'circle-small']}
          key="ok-high"
          label={`(f/4) ${t('legendsList.values.ok', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.excellent}
          icon={['fas', 'circle-small']}
          key="excellent"
          label={`(f/5.6) ${t('legendsList.values.excellent', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.ok}
          icon={['fas', 'circle-small']}
          key="ok-low"
          label={`(f/7.1) ${t('legendsList.values.ok', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.caution}
          icon={['fas', 'circle-small']}
          key="caution-low"
          label={`(f/8) ${t('legendsList.values.caution', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.critical}
          icon={['fas', 'circle-small']}
          key="critical-low"
          label={`(f/11) ${t('legendsList.values.critical', { ns: 'cloudProcessing' })}`}
        />,
      ];
    }

    if (legendsMode === 'shutterSpeed') {
      return [
        <LegendItem
          color={sfmViewerColors.legends.none}
          icon={['fas', 'circle-small']}
          key="none"
          label={t('legendsList.values.none', { ns: 'cloudProcessing' })}
        />,
        <LegendItem
          color={sfmViewerColors.legends.excellent}
          icon={['fas', 'circle-small']}
          key="excellent"
          label={`(>= 1/2000) ${t('legendsList.values.excellent', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.ok}
          icon={['fas', 'circle-small']}
          key="ok"
          label={`(>= 1/1000) ${t('legendsList.values.ok', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.caution}
          icon={['fas', 'circle-small']}
          key="caution"
          label={`(>= 1/500) ${t('legendsList.values.caution', { ns: 'cloudProcessing' })}`}
        />,
        <LegendItem
          color={sfmViewerColors.legends.critical}
          icon={['fas', 'circle-small']}
          key="critical"
          label={`(<= 1/500) ${t('legendsList.values.critical', { ns: 'cloudProcessing' })}`}
        />,
      ];
    }

    return [
      <LegendItem
        color={sfmViewerColors.cameras.default}
        icon={['fas', 'circle-small']}
        key="default"
        label={t('legendsList.images.default', { ns: 'cloudProcessing' })}
      />,
      <LegendItem
        color={sfmViewerColors.cameras.loaded}
        icon={['fas', 'circle-small']}
        key="loaded"
        label={t('legendsList.images.linkedLocally', { ns: 'cloudProcessing' })}
      />,
      <LegendItem
        color={sfmViewerColors.cameras.disabled}
        icon={['fas', 'circle-small']}
        key="disabled"
        label={t('legendsList.images.disabledUploading', { ns: 'cloudProcessing' })}
      />,
      <LegendItem
        color={sfmViewerColors.cameras.pending}
        icon={['fas', 'circle-small']}
        key="pending"
        label={t('legendsList.images.pending', { ns: 'cloudProcessing' })}
      />,
      <LegendItem
        color={sfmViewerColors.cameras.unaligned}
        icon={['fas', 'circle-small']}
        key="unaligned"
        label={t('legendsList.images.failedToAlign', { ns: 'cloudProcessing' })}
      />,
      <LegendItem
        color={sfmViewerColors.cursors.pinnedImageCursors}
        icon={['fas', 'xmark']}
        key="pinnedImageCursors"
        label={t('legendsList.images.pinnedMarker', { ns: 'cloudProcessing' })}
      />,
      <LegendItem
        color={sfmViewerColors.cursors.unpinnedImageCursors}
        icon={['fas', 'xmark']}
        key="unpinnedImageCursors"
        label={t('legendsList.images.unpinnedMarker', { ns: 'cloudProcessing' })}
      />,
    ];
  }, [legendsMode, t]);
  const gcpItems = useMemo<NonNullable<ReactNode>[]>(() => {
    return [
      <LegendItem
        color={sfmViewerColors.gcp.default}
        icon={['fas', 'circle-small']}
        key="default"
        label={t('legendsList.gcp.default', { ns: 'cloudProcessing' })}
      />,
      <LegendItem
        color={sfmViewerColors.cursors.gcpCursor}
        icon={['fas', 'circle-small']}
        key="gcpCursor"
        label={t('legendsList.gcp.active', { ns: 'cloudProcessing' })}
      />,
    ];
  }, [t]);
  return (
    <Stack spacing={1}>
      <Select
        options={legendsModeOptions}
        value={legendsMode}
        onChange={(e) => setLegendsMode(e.target.value as LegendsMode)}
      />
      <LabelledContainer fontWeight={600} text={t('imageList.title', { ns: 'cloudProcessing' })}>
        <List items={imageItems} />
      </LabelledContainer>

      <LabelledContainer fontWeight={600} text={t('gcpList.title', { ns: 'cloudProcessing' })}>
        <List items={gcpItems} />
      </LabelledContainer>
    </Stack>
  );
};

const LegendItem = ({
  icon,
  label,
  color,
}: {
  icon: IconProp;
  color: [number, number, number];
  label: string;
}) => {
  // Three.js Color.toArray() returns 0–1; CSS rgb() expects 0–255
  const cssColor = `rgb(${Math.round(color[0] * 255)}, ${Math.round(color[1] * 255)}, ${Math.round(color[2] * 255)})`;
  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <Icon color={cssColor} fixedWidth={true} icon={icon} />
      <span>{label}</span>
    </Stack>
  );
};
